import { nextTick, ref } from 'vue'
import type { Ref } from 'vue'
import { computePosition, flip, offset } from '@floating-ui/dom'
import { EMOJI_GROUP_NAMES_RU, EMOJI_STATIC_TEXTS_RU } from '../constants/emojiPicker'
import { useDelayedAction } from './useDelayedAction'

/** Задержка закрытия при уводе мыши — как в Telegram */
const CLOSE_ON_LEAVE_MS = 350
const PICKER_WIDTH = 352
const PICKER_HEIGHT = 435
const PICKER_GAP = 8

interface EmojiPickerOptions {
  isMobile?: Ref<boolean>
}

export const useEmojiPicker = (
  emojiBtnRef: Ref<HTMLElement | null>,
  messageInputRef: Ref<HTMLElement | null>,
  options: EmojiPickerOptions = {},
) => {
  const { isMobile } = options

  const showEmojiPicker = ref(false)
  const isPositioned = ref(false)
  const emojiPanelRef = ref<HTMLElement | null>(null)
  const emojiPickerPosition = ref({
    top: 0,
    left: 0,
    bottom: undefined as number | undefined,
  })

  const { schedule: scheduleClose, cancel: cancelClose } = useDelayedAction(CLOSE_ON_LEAVE_MS)

  const findEmojiButton = (): HTMLElement | null => {
    if (emojiBtnRef.value instanceof HTMLElement) {
      return emojiBtnRef.value
    }

    const ebr: any = emojiBtnRef.value
    if (ebr?.$el) {
      const component = ebr.$el
      return component.querySelector?.('button')
        || component.querySelector?.('[role="button"]')
        || component
    }
    if (ebr?.el) return ebr.el

    return document.querySelector('.emoji-picker-btn') as HTMLElement
  }

  /** Синхронная позиция — без прыжка из (0,0). */
  const positionFromButton = () => {
    const btnElement = findEmojiButton()
    if (!btnElement) {
      return
    }

    if (isMobile?.value) {
      emojiPickerPosition.value = { top: 0, left: 8, bottom: 100 }
      return
    }

    const rect = btnElement.getBoundingClientRect()
    let top = rect.top - PICKER_HEIGHT - PICKER_GAP
    if (top < PICKER_GAP) {
      top = rect.bottom + PICKER_GAP
    }

    let left = rect.right - PICKER_WIDTH
    if (left < PICKER_GAP) {
      left = PICKER_GAP
    }

    const maxLeft = window.innerWidth - PICKER_WIDTH - PICKER_GAP
    if (left > maxLeft) {
      left = Math.max(PICKER_GAP, maxLeft)
    }

    emojiPickerPosition.value = { top, left, bottom: undefined }
  }

  const calculatePosition = async () => {
    const btnElement = findEmojiButton()
    if (!btnElement) {
      isPositioned.value = true
      return
    }

    if (isMobile?.value) {
      emojiPickerPosition.value = { top: 0, left: 8, bottom: 100 }
      isPositioned.value = true
      return
    }

    if (!emojiPanelRef.value) {
      isPositioned.value = true
      return
    }

    const { x, y } = await computePosition(btnElement, emojiPanelRef.value, {
      placement: 'top-end',
      strategy: 'fixed',
      middleware: [offset(8), flip()],
    })

    emojiPickerPosition.value = { top: y, left: x, bottom: undefined }
    isPositioned.value = true
  }

  const close = () => {
    cancelClose()
    showEmojiPicker.value = false
    isPositioned.value = false
  }

  const toggle = () => {
    cancelClose()

    if (showEmojiPicker.value) {
      close()
      return
    }

    positionFromButton()
    isPositioned.value = false
    showEmojiPicker.value = true

    nextTick(() => {
      void calculatePosition()
    })
  }

  const resolveElement = (target: unknown): HTMLElement | null => {
    if (!target) {
      return null
    }
    if (target instanceof HTMLElement) {
      return target
    }

    const component = target as { $el?: HTMLElement; el?: HTMLElement }

    return component.$el ?? component.el ?? null
  }

  const insertTextAtCursor = (text: string, msg: Ref<string>) => {
    const root = resolveElement(messageInputRef.value)
    const input = root?.querySelector?.('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null

    if (input) {
      const start = input.selectionStart ?? msg.value.length
      const end = input.selectionEnd ?? msg.value.length
      msg.value = `${msg.value.slice(0, start)}${text}${msg.value.slice(end)}`
      const newPos = start + text.length
      nextTick(() => {
        input.focus()
        input.setSelectionRange(newPos, newPos)
      })
      return
    }

    msg.value += text
  }

  /** Вставляет emoji и оставляет пикер открытым (мультивыбор как в Telegram). */
  const onSelectEmoji = (emoji: any, msg: Ref<string>) => {
    const char = emoji?.i || emoji?.native || ''
    if (!char) return
    cancelClose()
    insertTextAtCursor(char, msg)
  }

  const onPanelEnter = () => {
    cancelClose()
  }

  const onPanelLeave = () => {
    scheduleClose(close)
  }

  const onDocumentClick = (e: MouseEvent) => {
    if (!showEmojiPicker.value) return

    const target = e.target as Node
    const panel = emojiPanelRef.value
    const btn = emojiBtnRef.value

    if (panel && panel.contains(target)) return

    const btnElement = (btn as { $el?: HTMLElement })?.$el || (btn as HTMLElement)
    if (btnElement && btnElement.contains && btnElement.contains(target)) return

    close()
  }

  return {
    showEmojiPicker,
    isPositioned,
    emojiPanelRef,
    emojiPickerPosition,
    emojiGroupNamesRu: EMOJI_GROUP_NAMES_RU,
    emojiStaticTextsRu: EMOJI_STATIC_TEXTS_RU,
    calculatePosition,
    toggle,
    close,
    onSelectEmoji,
    onPanelEnter,
    onPanelLeave,
    onDocumentClick,
  }
}
